import { useId } from "react"
import { formatFr } from "@/lib/utils"

/**
 * Briques partagées de l'affichage « historique d'une valeur dans le temps ».
 *
 * Écrites d'abord pour l'historique d'un record (1RM), reprises telles quelles par
 * l'historique d'une mesure corporelle : mêmes besoins (une courbe, une liste datée avec
 * l'écart à chaque étape), donc un seul rendu à maintenir. Ce qui diffère d'un cas à
 * l'autre passe en props : l'unité, le libellé d'étape, et la façon de lire un écart.
 */

export interface HistoryPoint {
  id: string
  /** Date du relevé, en ISO local (YYYY-MM-DD). */
  date: string
  value: number
  /** Libellé de l'étape (« Test initial »). Absent quand le relevé n'a pas de phase. */
  label?: string
  /**
   * Réserve sur la valeur, affichée en petit sous la date. Sert aux 1RM issus d'une série
   * arrêtée avant l'échec : la mesure compte normalement, mais elle sous-estime
   * probablement, et le lecteur doit le savoir sans avoir à le deviner.
   */
  note?: string
}

/**
 * Un écart n'a pas toujours un sens de progression.
 *
 * `progress` colore en vert/rouge — vrai pour un 1RM, où plus lourd est toujours mieux.
 * `neutral` reste gris : sur un tour de taille ou une masse grasse, le sens dépend de
 * l'objectif du client, et trancher à sa place afficherait un jugement faux la moitié du
 * temps. La section Mesures est déjà neutre ailleurs, on ne change pas de doctrine ici.
 */
export type DeltaTone = "progress" | "neutral"

/** Nombre nu, sans unité : les axes de la courbe sont trop étroits pour la porter. */
function formatTick(value: number): string {
  return formatFr(value, value % 1 === 0 ? 0 : 1)
}

function formatDelta(delta: number): string {
  return `${delta > 0 ? "+" : "−"}${formatFr(Math.abs(delta), Math.abs(delta) % 1 === 0 ? 0 : 1)}`
}

function formatTimelineDate(iso: string): string {
  const [y, m, d] = iso.split("T")[0].split("-").map(Number)
  if (!y || !m || !d) return "—"
  return new Date(y, m - 1, d).toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" })
}

// ── Courbe ──────────────────────────────────────────────────────────────────

const W = 300
const H = 130
const PAD_X = 24
const TOP = 20
const BOTTOM = 92
const BASELINE = 118

/**
 * Courbe des relevés successifs. Tracée à la main en SVG plutôt qu'avec Chart.js : une
 * poignée de points sans axes ni interaction ne justifie pas le poids de la librairie, et
 * le rendu doit coller au trait cyan du reste de l'app.
 *
 * L'abscisse est proportionnelle au TEMPS, pas au rang du relevé : espacer les points
 * régulièrement ferait passer six mois d'interruption pour une semaine. Les mesures
 * corporelles sont saisies à un rythme irrégulier, c'est là que ça compte.
 *
 * Les points ne sont pas tous étiquetés quand ils sont nombreux — les nombres se
 * chevaucheraient. Premier et dernier toujours, les autres seulement si la place le permet.
 */
export function HistoryChart({
  points, ariaLabel = "Évolution dans le temps",
}: { points: HistoryPoint[]; ariaLabel?: string }) {
  // L'id du dégradé doit être unique dans le document : deux courbes montées en même
  // temps (une page + une modale) partageraient sinon la même définition.
  const gradientId = `history-fill-${useId().replace(/:/g, "")}`

  if (points.length < 2) return null

  const values = points.map((p) => p.value)
  const min = Math.min(...values)
  const max = Math.max(...values)
  // Un plateau parfait diviserait par zéro : on le pose au milieu de la zone utile.
  const span = max - min || 1

  const times = points.map((p) => new Date(p.date).getTime())
  const tMin = Math.min(...times)
  const tMax = Math.max(...times)
  const tSpan = tMax - tMin

  const coords = points.map((p, i) => ({
    // Tous les relevés le même jour (tSpan = 0) : on retombe sur un espacement régulier.
    x: PAD_X + (tSpan > 0 ? (times[i] - tMin) / tSpan : i / (points.length - 1)) * (W - PAD_X * 2),
    y: max === min ? (TOP + BOTTOM) / 2 : BOTTOM - ((p.value - min) / span) * (BOTTOM - TOP),
    value: p.value,
  }))

  const line = coords.map((c, i) => `${i === 0 ? "M" : "L"}${c.x.toFixed(1)} ${c.y.toFixed(1)}`).join(" ")
  const area = `${line} L${coords[coords.length - 1].x.toFixed(1)} ${BASELINE} L${coords[0].x.toFixed(1)} ${BASELINE} Z`

  const dense = points.length > 8
  const labelled = pickLabelledPoints(coords.map((c) => c.x))

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="block w-full h-auto" role="img" aria-label={ariaLabel}>
      <g stroke="rgba(255,255,255,0.06)" strokeWidth="1">
        <path d={`M12 20h${W - 24}M12 55h${W - 24}M12 90h${W - 24}`} />
      </g>
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#06b6d4" stopOpacity="0.5" />
          <stop offset="100%" stopColor="#06b6d4" stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={area} fill={`url(#${gradientId})`} opacity="0.35" />
      <path d={line} fill="none" stroke="#06b6d4" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
      <g fill="#06b6d4">
        {coords.map((c, i) => (
          <circle key={i} cx={c.x} cy={c.y} r={i === coords.length - 1 ? 5 : dense ? 2.5 : 4} />
        ))}
      </g>
      <g fontFamily="ui-monospace, monospace" fontSize="10" textAnchor="middle">
        {coords.map((c, i) => {
          if (!labelled.has(i)) return null
          return (
            <text key={i} x={clampLabelX(c.x)} y={112} fill={i === coords.length - 1 ? "#67e8f9" : "#6b7280"}>
              {formatTick(c.value)}
            </text>
          )
        })}
      </g>
    </svg>
  )
}

/** Garde l'étiquette d'un point d'extrémité à l'intérieur du viewBox. */
function clampLabelX(x: number): number {
  return Math.min(Math.max(x, 16), W - 16)
}

/** Largeur confortable pour un nombre de quatre caractères en 10 px monospace. */
const MIN_LABEL_GAP = 52

/**
 * Choisit les points à étiqueter en fonction de leur ÉCART HORIZONTAL, pas de leur rang.
 *
 * Trier par rang (« un point sur trois ») paraît suffisant tant que les relevés sont
 * réguliers, mais l'abscisse est temporelle : deux relevés faits à trois jours d'intervalle
 * puis un trou de quatre mois donnent deux étiquettes superposées. On part donc du dernier
 * point — celui qu'on veut toujours lire, c'est la valeur courante — et on remonte en ne
 * gardant que ce qui tient à distance.
 */
function pickLabelledPoints(xs: number[]): Set<number> {
  const kept = new Set<number>()
  let lastX = Infinity
  for (let i = xs.length - 1; i >= 0; i--) {
    if (i === xs.length - 1 || lastX - xs[i] >= MIN_LABEL_GAP) {
      kept.add(i)
      lastX = xs[i]
    }
  }
  return kept
}

// ── Liste datée ─────────────────────────────────────────────────────────────

/**
 * Relevés du plus récent au plus ancien — celui qu'on vient chercher est en haut — avec
 * l'écart par rapport au relevé précédent à chaque étape.
 */
export function HistoryTimeline({
  points, formatValue, deltaTone = "progress",
}: {
  points: HistoryPoint[]
  formatValue: (value: number) => string
  deltaTone?: DeltaTone
}) {
  return (
    <>
      {[...points].reverse().map((p, i, arr) => {
        const previous = arr[i + 1]
        const delta = previous ? p.value - previous.value : null
        const deltaColor = deltaTone === "neutral"
          ? "#94a3b8"
          : (delta ?? 0) > 0 ? "#4ade80" : "#f87171"

        return (
          <div
            key={p.id}
            className="flex items-center gap-3.5 px-4 py-3.5"
            style={{ borderBottom: i < arr.length - 1 ? "1px solid rgba(30,41,59,0.35)" : "none" }}
          >
            <span
              className="w-[9px] h-[9px] rounded-full shrink-0"
              style={{
                background: i === 0 ? "#06b6d4" : "#334155",
                boxShadow: i === 0 ? "0 0 10px -1px #06b6d4" : undefined,
              }}
            />
            <span className="flex-1 min-w-0">
              {p.label ? (
                <>
                  <span className="block text-[14px] font-medium text-gray-200">{p.label}</span>
                  <span className="block text-[11.5px] text-gray-500 mt-0.5 tnum">
                    {formatTimelineDate(p.date).toUpperCase()}
                  </span>
                </>
              ) : (
                <span className="block text-[14px] font-medium text-gray-200 tnum">
                  {formatTimelineDate(p.date)}
                </span>
              )}
              {p.note && (
                <span className="block text-[11px] mt-0.5" style={{ color: "#f59e0b" }}>{p.note}</span>
              )}
            </span>
            <span className="text-right shrink-0">
              <span className="block text-[15px] font-bold text-white tnum">{formatValue(p.value)}</span>
              {delta != null && delta !== 0 && (
                <span className="block text-[10.5px] mt-0.5 tnum" style={{ color: deltaColor }}>
                  {formatDelta(delta)}
                </span>
              )}
            </span>
          </div>
        )
      })}
    </>
  )
}

/**
 * Bandeau du haut : la valeur courante en grand et l'écart total depuis le premier relevé.
 * Rendu vide de tout habillage — l'appelant le pose où il veut (pleine page ou modale).
 */
export function HistoryHero({
  points, formatValue, deltaTone = "progress", caption,
}: {
  points: HistoryPoint[]
  formatValue: (value: number) => string
  deltaTone?: DeltaTone
  caption?: string
}) {
  const current = points.length > 0 ? points[points.length - 1] : null
  const first = points.length > 1 ? points[0] : null
  const total = current && first ? current.value - first.value : null
  const totalColor = deltaTone === "neutral"
    ? "#cbd5e1"
    : (total ?? 0) > 0 ? "#4ade80" : "#f87171"

  return (
    <div className="text-center">
      <p className="text-[38px] font-bold text-white leading-none tracking-tight tnum">
        {current ? formatValue(current.value) : "—"}
      </p>
      {total != null && total !== 0 && (
        <p className="text-[12.5px] text-gray-400 mt-2 tnum">
          <b style={{ color: totalColor }}>{formatDelta(total)}</b> depuis le premier relevé
        </p>
      )}
      {caption && <p className="text-[11.5px] text-gray-500 mt-1.5">{caption}</p>}
    </div>
  )
}
